import React, { useCallback, useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Transition } from '@headlessui/react';
import { add, format, sub } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';
import { AditionInitialization } from '@components/atoms/Adition/AditionInitialization';
import { Button } from '@components/atoms/Button/Button';
import { Link } from '@components/atoms/Link/Link';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { EntryCarousel } from '@components/molecules/EntryCarousel/EntryCarousel';
import { ShowtimeDetails } from '@components/molecules/ShowtimeDetails/ShowtimeDetails';
import { ChannelDay } from '@components/organisms/Channel/ChannelDay';
import { Default as DefaultLayout } from '@components/templates/layouts/Default';
import { formatDate, formatSlugDate, getValidatedUrlDate } from '@utils/dateHelpers';
import { IconStar } from '@src/assets/icon-star';
import { useTvGuideStore } from '@src/stores/tvguide';
import { adPositions } from '@lib/adition/adPositions';
import { useWindowSize } from '@lib/hooks/useWindowSize';
import { IconBall } from '@assets/icon-ball';
import { IconLeft } from '@assets/icon-left';
import { IconPhone } from '@assets/icon-phone';
import { IconRight } from '@assets/icon-right';

export function ChannelDetailsContent({ data }) {
  const openEntryData = useTvGuideStore((state) => state.openEntryData);
  const setOpenEntryData = useTvGuideStore((state) => state.setOpenEntryData);
  const { channels, oewa } = data;
  const [customMetadata, setCustomMetadata] = useState({});
  const [activeChannel, setActiveChannel] = useState(data?.activeChannel);
  const [channelTarget, setChannelTarget] = useState(data?.activeChannel?.id);
  const [activeDate, setActiveDate] = useState(getValidatedUrlDate(data.activeDay));
  const router = useRouter();
  const size = useWindowSize();

  const updateCustomMetaData = useCallback(
    (entry = null) => {
      const metaEntry = entry || openEntryData?.entry;
      const metaChannel = activeChannel;
      const meta = {};

      const formattedDate = formatDate(activeDate.toDateString(), {
        dateStyle: 'medium',
      });

      if (metaEntry) {
        meta['meta_title'] = `${metaEntry.title}${
          metaChannel?.name ? ' | ' + metaChannel.name : ''
        }|${formattedDate} | TV-Programm | TV-MEDIA`;
        if (metaEntry.summary) {
          meta['meta_description'] =
            metaEntry.summary?.length > 157 ? metaEntry.summary?.slice(0, 155) + '..' : metaEntry.summary;
        }
      } else {
        meta['meta_title'] = `${metaChannel?.name} TV-Programm auf TV-MEDIA.at`;
        meta['meta_description'] =
          `Alle Sendungen bei ${metaChannel?.name} heute und morgen im Überblick. Das gesamte TV-Programm von TV-MEDIA von ${metaChannel?.name} heute und morgen.`;
      }

      setCustomMetadata(meta);
    },
    [activeChannel, activeDate, openEntryData],
  );

  const updateSlug = useCallback(
    async (entry = null) => {
      updateCustomMetaData(entry);
      const today = formatSlugDate(toZonedTime(new Date(), 'Europe/Vienna'));
      const activeDaySlug = formatSlugDate(toZonedTime(new Date(activeDate), 'Europe/Vienna'));
      const slugify = (await import('react-slugify')).default;
      const entrySlugExtra = entry ? slugify(`${activeChannel?.slug}-${entry.title}`) : '';

      const slug = [
        activeChannel?.slug,
        ...(activeDaySlug === today ? [] : [activeDaySlug]),
        ...(entry?.event_id ? [`${entrySlugExtra}-${entry.event_id}`] : []),
      ];

      router.replace(
        {
          pathname: '/sender/[...slug]',
          query: {
            slug,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    [activeChannel, activeDate, router, updateCustomMetaData],
  );

  useEffect(() => {
    setOpenEntryData(data?.openEntry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    router.replace(
      {
        pathname: '/sender/[...slug]',
        query: {
          slug: data?.activeChannel?.slug,
        },
      },
      undefined,
      { shallow: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEntryData]);

  useEffect(() => {
    if (router.isReady) {
      setTimeout(() => {
        updateSlug(openEntryData?.entry);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel, router.isReady, activeDate]);

  return (
    <DefaultLayout>
      <Oewa
        pageKey={oewa?.page_key || 'RedCont'}
        categoryKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'FilmUndKino'}
      />
      <Head>
        <title>{activeChannel?.name}</title>
        {Meta({ ...activeChannel?.metadata, ...customMetadata })}
      </Head>
      <AditionInitialization tags={adPositions} />
      <div className="relative grid">
        <div className="row-start-3 mt-16 grid w-full min-w-0 flex-wrap gap-4 justify-self-end md:row-start-auto md:mt-0 md:flex md:justify-center md:px-3">
          <select
            className="md:giant"
            value={activeChannel?.id}
            onChange={(item) => {
              setChannelTarget(item?.target.value);
            }}
          >
            {channels?.map((channel) => (
              <option key={channel.id} value={channel.id}>
                {channel.name}
              </option>
            ))}
          </select>
        </div>
        <div className="row-start-1 mt-1 md:row-start-auto">
          {channels?.length > 0 && (
            <EntryCarousel
              target={channelTarget}
              type="image"
              primaryProperty="localLogo"
              entries={channels}
              altProperty="name"
              isTvChannels
              classProps={{
                entry:
                  'relative flex items-center justify-center content-center w-full h-full max-w-[150px] max-h-20 md:max-w-[200px] md:max-h-24',
                entryContainer:
                  'w-full relative aspect-video rounded-xl overflow-hidden border-gray-650/65 streaming-entry-bg flex items-center justify-center content-center text-3xl',
              }}
              onEntryChange={(channel) => {
                setChannelTarget(null);
                setActiveChannel(channel);
              }}
            />
          )}
        </div>
        <div className="row-start-2 mx-auto mt-16 grid max-w-[870px] gap-4 border-y border-gray-650/65 bg-gray-820/70 p-6 md:row-start-auto md:mt-0 md:grid-cols-[1fr,2fr]">
          <div>
            <Title level={1} classProps={{ heading: 'text-2xl lg:text-2xl' }}>
              {activeChannel?.name}
            </Title>
            <div className="mt-5 flex flex-wrap gap-x-7 gap-y-2 text-sm md:grid">
              {activeChannel?.url && (
                <div className="flex items-center text-white">
                  <IconBall classProps={{ root: 'w-4 h-4 mr-2' }} />{' '}
                  <Link href={activeChannel.url}>{activeChannel.url}</Link>
                </div>
              )}
              {activeChannel?.phone && (
                <div className="flex items-center">
                  <IconPhone classProps={{ root: 'w-4 h-4 mr-1' }} /> <span>{activeChannel.phone}</span>
                </div>
              )}
            </div>
          </div>
          <div className="h-px w-full bg-gray-650/25 md:hidden"></div>
          <div>
            <p>
              Du findest hier das aktuelle TV-Programm für {activeChannel?.name} sowie das Programm für heute und
              morgen.
            </p>
            <p className="text-gray-400">
              Des Weiteren kannst Du sehen, welche Sendung derzeit ausgestrahlt wird und ob Du gestern eine verpasst
              hast, die Du möglicherweise in der Mediathek streamen kannst.
            </p>
          </div>
        </div>
        <div className="my-12 text-center">
          <Button
            link="/favoriten/sender"
            buttonStyle="primary"
            title="Favoriten bearbeiten"
            PreElement={<IconStar classProps={{ root: 'w-4 h-4' }} filled />}
          />
        </div>
        {activeChannel?.id && (
          <>
            {size.width <= 992 && (
              <div className="grid grid-cols-2 py-5">
                <div
                  className="grid cursor-pointer items-center"
                  onClick={() => {
                    setActiveDate((prev) => {
                      return sub(prev, { days: 1 });
                    });
                    setTimeout(() => {
                      updateSlug();
                    });
                  }}
                >
                  <IconLeft
                    classProps={{
                      root: 'hover:opacity-60 opacity-100 border-none w-5 h-5 lg:w-4 lg:h-4 transition-opacity duration-150',
                    }}
                  />
                </div>
                <div
                  className="grid cursor-pointer items-center justify-self-end"
                  onClick={() => {
                    setActiveDate((prev) => {
                      return add(prev, { days: 1 });
                    });
                    setTimeout(() => {
                      updateSlug();
                    });
                  }}
                >
                  <IconRight
                    classProps={{
                      root: 'hover:opacity-60 opacity-100 border-none w-5 h-5 lg:w-4 lg:h-4 transition-opacity duration-150',
                    }}
                  />
                </div>
              </div>
            )}
            <article
              className={twMerge(
                'mx-auto grid w-full max-w-screen-lg gap-4 md:grid-cols-2 lg:grid-cols-3',
                size.width > 992 && 'mt-10',
              )}
            >
              {size.width > 992 && (
                <ChannelDay
                  key={format(sub(activeDate, { days: 1 }), 'yyyyMMdd') + '-position-1'}
                  id={activeChannel?.id}
                  date={sub(activeDate, { days: 1 })}
                  onAction={(showtimeEntry) => {
                    setOpenEntryData(showtimeEntry, activeChannel);
                  }}
                  showButton="prev"
                  onButtonClick={() => {
                    setActiveDate((prev) => {
                      return sub(prev, { days: 1 });
                    });
                  }}
                />
              )}
              <ChannelDay
                key={format(activeDate, 'yyyyMMdd') + '-position-2'}
                id={activeChannel?.id}
                date={activeDate}
                onAction={(showtimeEntry) => {
                  setOpenEntryData(showtimeEntry, activeChannel);
                }}
              />
              {size.width > 768 && (
                <ChannelDay
                  key={format(add(activeDate, { days: 1 }), 'yyyyMMdd') + '-position-3'}
                  id={activeChannel?.id}
                  date={add(activeDate, { days: 1 })}
                  onAction={(showtimeEntry) => {
                    setOpenEntryData(showtimeEntry, activeChannel);
                  }}
                  onButtonClick={() => {
                    setActiveDate((prev) => {
                      return add(prev, { days: 1 });
                    });
                  }}
                  showButton={size.width > 992 ? 'next' : null}
                />
              )}
            </article>
          </>
        )}
        <Transition
          as="div"
          className="relative z-70"
          enter="transition ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          show={openEntryData?.entry !== null}
          appear
        >
          <ShowtimeDetails
            entryEventId={openEntryData?.entry?.event_id}
            onFetchComplete={(entry) => {
              updateSlug(entry);
            }}
            channel={activeChannel}
            key={openEntryData?.entry?.event_id}
          />
        </Transition>
      </div>
    </DefaultLayout>
  );
}
